import { z } from "zod";

import {
  CardDefinitionIdSchema,
  DefinitionVersionSchema,
  LandTypeIdSchema,
  NonNegativeIntegerSchema,
  PositiveIntegerSchema,
  SCHEMA_VERSION,
} from "./primitives.js";
import {
  ConditionReferenceSchema,
  EffectReferenceSchema,
  MandateReferenceSchema,
  TriggerReferenceSchema,
} from "./references.js";
import { ArmyUpkeepSchema, ResourceCostSchema, ResourceProductionSchema } from "./resources.js";
import { findLandStageControlDecrease } from "./validation/validate-land-stages.js";

export const CARD_TYPES = ["land", "commander", "army", "event", "policy", "conspiracy"] as const;

export const CardTypeSchema = z.enum(CARD_TYPES);

const RulesTextSchema = z.string().trim().min(1).max(8000);
const CardNameSchema = z.string().trim().min(1).max(120);

const BaseCardFields = {
  schemaVersion: z.literal(SCHEMA_VERSION),
  definitionVersion: DefinitionVersionSchema,
  id: CardDefinitionIdSchema,
  name: CardNameSchema,
  cost: ResourceCostSchema,
  rulesText: RulesTextSchema,
};

const createLandStageSchema = <TStage extends 1 | 2 | 3>(stage: TStage) =>
  z
    .object({
      stage: z.literal(stage),
      title: z.string().trim().min(1).max(120),
      maximumControl: PositiveIntegerSchema,
      production: ResourceProductionSchema,
      effects: z.array(EffectReferenceSchema),
    })
    .strict();

export const LandStageISchema = createLandStageSchema(1);
export const LandStageIISchema = createLandStageSchema(2);
export const LandStageIIISchema = createLandStageSchema(3);

export const LandStagesSchema = z.tuple([LandStageISchema, LandStageIISchema, LandStageIIISchema]);

const LandCardDefinitionObjectSchema = z
  .object({
    ...BaseCardFields,
    type: z.literal("land"),
    landTypeId: LandTypeIdSchema,
    developmentId: CardDefinitionIdSchema,
    stages: LandStagesSchema,
  })
  .strict();

function validateLandDefinition(
  value: z.infer<typeof LandCardDefinitionObjectSchema>,
  context: z.RefinementCtx,
): void {
  if (value.developmentId !== value.id) {
    context.addIssue({
      code: "custom",
      path: ["developmentId"],
      message: "must equal the Land card ID so matching copies share one development identity",
    });
  }

  const decrease = findLandStageControlDecrease(value.stages);
  if (decrease !== null) {
    context.addIssue({
      code: "custom",
      path: ["stages", decrease.currentIndex, "maximumControl"],
      message:
        "must be greater than or equal to the previous Stage maximum Control (temporary FK-RULE-007 policy)",
    });
  }
}

export const LandCardDefinitionSchema =
  LandCardDefinitionObjectSchema.superRefine(validateLandDefinition);

const MandatesSchema = z
  .array(MandateReferenceSchema)
  .min(1)
  .superRefine((mandates, context) => {
    const firstIndexById = new Map<string, number>();
    mandates.forEach((mandate, index) => {
      const firstIndex = firstIndexById.get(mandate.mandateId);
      if (firstIndex === undefined) {
        firstIndexById.set(mandate.mandateId, index);
        return;
      }

      context.addIssue({
        code: "custom",
        path: [index, "mandateId"],
        message: `duplicates mandates[${firstIndex}].mandateId`,
      });
    });
  });

export const CommanderCardDefinitionSchema = z
  .object({
    ...BaseCardFields,
    type: z.literal("commander"),
    stats: z
      .object({
        attack: NonNegativeIntegerSchema,
        guard: NonNegativeIntegerSchema,
        armyCapacity: NonNegativeIntegerSchema.max(5, "Commander Army Capacity cannot exceed 5"),
      })
      .strict(),
    mandates: MandatesSchema,
  })
  .strict();

export const ArmyCardDefinitionSchema = z
  .object({
    ...BaseCardFields,
    type: z.literal("army"),
    stats: z
      .object({
        armySize: PositiveIntegerSchema.max(5),
        attack: NonNegativeIntegerSchema,
        guard: NonNegativeIntegerSchema,
      })
      .strict(),
    upkeep: ArmyUpkeepSchema,
    effects: z.array(EffectReferenceSchema),
  })
  .strict();

export const EventResolutionSchema = z
  .object({
    effects: z.array(EffectReferenceSchema).min(1),
    destination: z.enum(["discard", "exhaust"]),
  })
  .strict();

export const EnhancedEventResolutionSchema = z
  .object({
    condition: ConditionReferenceSchema,
    mode: z.enum(["additional", "instead"]),
    effects: z.array(EffectReferenceSchema).min(1),
  })
  .strict();

export const EventCardDefinitionSchema = z
  .object({
    ...BaseCardFields,
    type: z.literal("event"),
    resolution: EventResolutionSchema,
    enhancedResolution: EnhancedEventResolutionSchema.optional(),
  })
  .strict();

export const PolicyCardDefinitionSchema = z
  .object({
    ...BaseCardFields,
    type: z.literal("policy"),
    duration: z.literal("while-active"),
    effects: z.array(EffectReferenceSchema).min(1),
  })
  .strict();

export const ConspiracyCardDefinitionSchema = z
  .object({
    ...BaseCardFields,
    type: z.literal("conspiracy"),
    trigger: TriggerReferenceSchema,
    resolution: EventResolutionSchema,
  })
  .strict();

const CardDefinitionUnionSchema = z.discriminatedUnion("type", [
  LandCardDefinitionObjectSchema,
  CommanderCardDefinitionSchema,
  ArmyCardDefinitionSchema,
  EventCardDefinitionSchema,
  PolicyCardDefinitionSchema,
  ConspiracyCardDefinitionSchema,
]);

export const CardDefinitionSchema = CardDefinitionUnionSchema.superRefine((value, context) => {
  if (value.type === "land") {
    validateLandDefinition(value, context);
  }
});

export type ArmyCardDefinition = z.infer<typeof ArmyCardDefinitionSchema>;
export type CardDefinition = z.infer<typeof CardDefinitionSchema>;
export type CardType = z.infer<typeof CardTypeSchema>;
export type CommanderCardDefinition = z.infer<typeof CommanderCardDefinitionSchema>;
export type ConspiracyCardDefinition = z.infer<typeof ConspiracyCardDefinitionSchema>;
export type EnhancedEventResolution = z.infer<typeof EnhancedEventResolutionSchema>;
export type EventCardDefinition = z.infer<typeof EventCardDefinitionSchema>;
export type EventResolution = z.infer<typeof EventResolutionSchema>;
export type LandCardDefinition = z.infer<typeof LandCardDefinitionSchema>;
export type LandStageI = z.infer<typeof LandStageISchema>;
export type LandStageII = z.infer<typeof LandStageIISchema>;
export type LandStageIII = z.infer<typeof LandStageIIISchema>;
export type PolicyCardDefinition = z.infer<typeof PolicyCardDefinitionSchema>;
